import {
    IGroupedComboBoxItem,
    IGroupedComboBoxGroup,
} from '../GroupedComboBox/GroupedComboBox.types.shared';

function filterItems(
    items: IGroupedComboBoxItem[],
    regEx: RegExp,
    searchText: string
): IGroupedComboBoxItem[] {
    return items.filter<IGroupedComboBoxItem>((item): item is IGroupedComboBoxItem => {
        if (item.excludeFromSearch) {
            return false;
        }

        let matching = regEx.test(item.text);

        // if this item doesn't match the regex, check all the children items within the sections.
        // However, we only do so if the searchText has at least 2 characters.
        if (!matching && searchText.length > 1 && item.sections) {
            matching = item.sections.some((section) =>
                section.items.some((item) => !item.excludeFromSearch && regEx.test(item.text))
            );
        }

        return matching;
    });
}

/**
 * Checks if the specified groups has any group element that has non-empty 'items' collection.
 * @param groups - the groups to check
 */
export function hasNonEmptyGroup(groups: IGroupedComboBoxGroup[]): boolean {
    return groups.some((g) => g.items.length > 0);
}

function filterEmptyGroups(groups: IGroupedComboBoxGroup[]): IGroupedComboBoxGroup[] {
    const nonEmptyGroups = groups.filter((g) => g.items.length > 0);

    // important: if there is no empty group, we return the
    // original array in order to preserve React caching (e.g. in useCallback(), useMemo(), etc. )
    return nonEmptyGroups.length === groups.length ? groups : nonEmptyGroups;
}

export function filterGroupsBySubstringSearch(
    groups: IGroupedComboBoxGroup[],
    searchText?: string
): IGroupedComboBoxGroup[] {
    if (!searchText) {
        return filterEmptyGroups(groups);
    }

    // 'searchText' may contain special regex characters.
    // To avoid interpreting those, we'll escape them first.
    const regEx = new RegExp(searchText.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'), 'i');

    const searchResults = groups.map<IGroupedComboBoxGroup>((g) => ({
        ...g,
        initiallyCollapsed: false, // when doing a search, expanding the groups
        items: filterItems(g.items, regEx, searchText),
    }));

    return filterEmptyGroups(searchResults);
}
